import type { ProductTypeBorrowerType, ProductTypeRequest } from '@/api/backend/public/product-type';
import { AdditionalFundsPurpose, BorrowerType, PropertyPurpose, PropertyType } from '@/helpers/const';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import type { LoanApplication } from '@/types/LoanApplication';

export type ApplicationBorrowerTypeApi = ProductTypeBorrowerType;

/** Minimal property shape for product-matrix dependency checks (aligned with techlend-be ProductTypeSharedService). */
export type ProductMatrixPropertySlice = {
  propertyType?: string;
  propertyPurpose?: string;
};

type IncomingDominant = 'ResidentialOO' | 'ResidentialInv' | 'CommercialOO' | 'CommercialInv' | null;

/**
 * When true, the public product-type matrix can return a classification that depends on application-level
 * borrower type; the property-details step should not apply a property-only result (wait for borrower groups).
 *
 * Mirrors branches in techlend-be `ProductTypeSharedService` (`determineBNSL` / `determineSingleSecurity`)
 * where `borrowerType` changes the outcome vs leaving it unset.
 */
export function productMatrixNeedsBorrowerTypeForProperties(
  incomingProperties: ProductMatrixPropertySlice[],
  outgoingProperties: ProductMatrixPropertySlice[],
  supportingProperties: ProductMatrixPropertySlice[] | undefined,
): boolean {
  const outgoing = [...outgoingProperties, ...(supportingProperties ?? [])];

  if (incomingProperties.length > 0) {
    return bnslProductMatrixDependsOnBorrowerType(incomingProperties[0], outgoing);
  }

  return singleSecurityProductMatrixDependsOnBorrowerType(outgoing);
}

function getDominantIncomingType(firstIncoming: ProductMatrixPropertySlice | undefined): IncomingDominant {
  if (!firstIncoming?.propertyType || !firstIncoming?.propertyPurpose) {
    return null;
  }
  const { propertyType: pt, propertyPurpose: pp } = firstIncoming;
  if (pt === PropertyType.RESIDENTIAL) {
    if (pp === PropertyPurpose.OWNER_OCCUPIED) return 'ResidentialOO';
    if (pp === PropertyPurpose.INVESTMENT_PROPERTY) return 'ResidentialInv';
  }
  if (pt === PropertyType.COMMERCIAL) {
    if (pp === PropertyPurpose.OWNER_OCCUPIED) return 'CommercialOO';
    if (pp === PropertyPurpose.INVESTMENT_PROPERTY) return 'CommercialInv';
  }
  return null;
}

function hasResidentialOwnerOccupied(properties: ProductMatrixPropertySlice[]): boolean {
  return properties.some(
    (p) => p.propertyType === PropertyType.RESIDENTIAL && p.propertyPurpose === PropertyPurpose.OWNER_OCCUPIED,
  );
}

function hasAnyResidential(properties: ProductMatrixPropertySlice[]): boolean {
  return properties.some((p) => p.propertyType === PropertyType.RESIDENTIAL);
}

function hasAnyCommercial(properties: ProductMatrixPropertySlice[]): boolean {
  return properties.some((p) => p.propertyType === PropertyType.COMMERCIAL);
}

function bnslProductMatrixDependsOnBorrowerType(
  firstIncoming: ProductMatrixPropertySlice | undefined,
  outgoing: ProductMatrixPropertySlice[],
): boolean {
  const incomingType = getDominantIncomingType(firstIncoming);

  if (outgoing.length === 0) {
    return false;
  }

  const hasOutgoingResOO = hasResidentialOwnerOccupied(outgoing);
  const hasAnyOutgoingResidential = hasAnyResidential(outgoing);
  const isAllOutgoingCommercial = outgoing.length > 0 && !hasAnyOutgoingResidential;

  if (incomingType === 'ResidentialOO') {
    return false;
  }

  if (incomingType === 'ResidentialInv') {
    if (hasAnyOutgoingResidential) {
      return false;
    }
    return true;
  }

  if (incomingType === 'CommercialOO') {
    if (hasOutgoingResOO) {
      return false;
    }
    if (isAllOutgoingCommercial) {
      return false;
    }
    return true;
  }

  if (incomingType === 'CommercialInv') {
    if (hasOutgoingResOO) {
      return false;
    }
    if (isAllOutgoingCommercial) {
      return false;
    }
    return true;
  }

  return false;
}

function singleSecurityProductMatrixDependsOnBorrowerType(outgoing: ProductMatrixPropertySlice[]): boolean {
  if (outgoing.length === 0) {
    return false;
  }
  return hasAnyResidential(outgoing) && hasAnyCommercial(outgoing);
}

const mapPropertySlice = (p: { propertyType?: string; propertyPurpose?: string }) => ({
  propertyType: p.propertyType,
  propertyPurpose: p.propertyPurpose,
});

/**
 * Derives application-level borrower type for the product matrix and CRM (matches techlend-be BorrowerType).
 * - All individuals → `individual`
 * - All company/trust (entities only) → `company`, `company_trust`, or `company` per group mix
 * - Both individuals and entities → `individual_and_company_trust`
 */
export function computeApplicationBorrowerTypeFromBorrowerGroups(
  groups: BorrowerGroup[],
): ApplicationBorrowerTypeApi | undefined {
  if (!groups.length) {
    return undefined;
  }

  let hasIndividual = false;
  let hasCompany = false;
  let hasTrust = false;

  for (const g of groups) {
    if (g.type === BorrowerType.INDIVIDUAL) {
      hasIndividual = true;
    } else if (g.type === BorrowerType.COMPANY) {
      hasCompany = true;
    } else if (g.type === BorrowerType.TRUST) {
      hasTrust = true;
    }
  }

  const hasEntity = hasCompany || hasTrust;

  if (hasIndividual && hasEntity) {
    return 'individual_and_company_trust';
  }
  if (hasIndividual) {
    return 'individual';
  }
  if (hasCompany && hasTrust) {
    return 'company_trust';
  }
  if (hasTrust) {
    return 'company_trust';
  }
  if (hasCompany) {
    return 'company_trust';
  }

  return undefined;
}

export interface BuildProductTypeRequestOptions {
  borrowerType?: ApplicationBorrowerTypeApi;
  incomingProperties?: LoanApplication['incomingProperties'];
  outgoingProperties?: LoanApplication['outgoingProperties'];
  supportingProperties?: LoanApplication['supportingProperties'];
}

/**
 * Builds the public product-type API payload from the loan application, including single-security
 * flags from additional funds / loan purpose where applicable.
 */
export function buildProductTypeRequestFromLoanApp(
  loanApp: LoanApplication,
  options?: BuildProductTypeRequestOptions,
): ProductTypeRequest {
  const incoming = options?.incomingProperties ?? loanApp.incomingProperties;
  const outgoing = options?.outgoingProperties ?? loanApp.outgoingProperties;
  const supporting = options?.supportingProperties ?? loanApp.supportingProperties ?? [];

  const reason =
    loanApp.additionalFundsRequiredProperties?.reasonForFundsOption ??
    (loanApp.additionalFundsRequired as { reasonForFundsOption?: string } | undefined)?.reasonForFundsOption;
  const isPurchaseRetirementVillage =
    reason === AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME;

  let loanPurpose: ProductTypeRequest['loanPurpose'];
  if (reason === AdditionalFundsPurpose.BUSINESS_PURPOSE) {
    loanPurpose = 'business_purpose';
  } else if (reason === AdditionalFundsPurpose.COMPLETE_PROJECT_FINANCE) {
    loanPurpose = 'complete_project_finance';
  }

  return {
    incomingProperties: incoming.length ? incoming.map(mapPropertySlice) : undefined,
    outgoingProperties: outgoing.map(mapPropertySlice),
    supportingProperties: supporting.length ? supporting.map(mapPropertySlice) : undefined,
    ...(isPurchaseRetirementVillage ? { isPurchaseRetirementVillage: true } : {}),
    ...(loanPurpose ? { loanPurpose } : {}),
    ...(options?.borrowerType ? { borrowerType: options.borrowerType } : {}),
  };
}
